// AidWaive, affordability scoring + Hidden Gem helpers.
//
// All scoring is pure JavaScript and runs offline. AI is called at most
// twice per session (once for the affordability explanation, once for
// the batched Hidden Gem blurbs) and every response is cached in
// localStorage by an input hash so identical inputs never hit Claude
// twice.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Income brackets (the "AidWaive Affordability" tool uses 4 buckets;
// the Net Price tool uses 6. Keep these maps in sync with the backend.)
export const INCOME_BRACKETS = [
  { value: "0_30k",   label: "Under $30,000",          midpoint: 22000,  scorecardKey: "under_30k" },
  { value: "30_60k",  label: "$30,000 to $60,000",     midpoint: 45000,  scorecardKey: "30k_48k"   },
  { value: "60_100k", label: "$60,000 to $100,000",    midpoint: 80000,  scorecardKey: "48k_75k"   },
  { value: "100k+",   label: "Over $100,000",          midpoint: 150000, scorecardKey: "over_110k" },
];

export const GPA_BRACKETS = [
  { value: "below_2_5", label: "Below 2.5",       midpoint: 2.2 },
  { value: "2_5_3_0",   label: "2.5 to 3.0",      midpoint: 2.75 },
  { value: "3_0_3_5",   label: "3.0 to 3.5",      midpoint: 3.25 },
  { value: "3_5_4_0",   label: "3.5 to 4.0+",     midpoint: 3.8  },
];

export const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

// Map our 4 affordability brackets onto the 5 Scorecard brackets. We
// pick the closest one. "100k+" maps to "over_110k" so we don't
// undercount aid for upper-middle-class families.
export function netPriceForBracket(school, bracketValue) {
  if (!school || !school.netPriceByIncome) return null;
  const meta = INCOME_BRACKETS.find(b => b.value === bracketValue);
  if (!meta) return null;
  return school.netPriceByIncome[meta.scorecardKey] ?? school.avgNetPrice ?? null;
}

/**
 * Affordability grade: ratio of annual net price to annual income.
 *   A: under 10%, B: 10-20%, C: 20-30%, D: 30-40%, F: 40%+.
 */
export function gradeForRatio(netPrice, incomeMidpoint) {
  if (!netPrice || !incomeMidpoint) return { grade: "—", ratio: 0 };
  const ratio = netPrice / incomeMidpoint;
  let grade = "F";
  if (ratio < 0.10) grade = "A";
  else if (ratio < 0.20) grade = "B";
  else if (ratio < 0.30) grade = "C";
  else if (ratio < 0.40) grade = "D";
  return { grade, ratio };
}

export function gradeColors(grade) {
  switch (grade) {
    case "A": return { bg: "#ecfdf5", border: "#6ee7b7", text: "#15803d", solid: "#15803d" };
    case "B": return { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af", solid: "#1d4ed8" };
    case "C": return { bg: "#fefce8", border: "#fde047", text: "#854d0e", solid: "#a16207" };
    case "D": return { bg: "#ffedd5", border: "#fdba74", text: "#9a3412", solid: "#c2410c" };
    case "F": return { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b", solid: "#b91c1c" };
    default:  return { bg: "#f3f4f6", border: "#d1d5db", text: "#374151", solid: "#374151" };
  }
}

/**
 * Estimated monthly payment if the family fully borrows 4× the annual
 * net price, on a 10-year term at 6.5% APR (Direct Stafford-ish).
 */
export function monthlyLoanPayment(annualNetPrice, years = 10, apr = 0.065) {
  if (!annualNetPrice) return 0;
  const principal = annualNetPrice * 4;
  const r = apr / 12;
  const n = years * 12;
  if (r === 0) return Math.round(principal / n);
  return Math.round((principal * r) / (1 - Math.pow(1 + r, -n)));
}

// ────────────────────────────────────────────────────────────────────
// Hidden Gem scoring
//
// Score each candidate on:
//   - Net price relative to the user's state flagship (lower = better)
//   - Graduation rate (>= 60% required to qualify)
//   - Similarity to dream schools by region + size
// Total score is in [0, 100]. We sort desc and return top 5.
// ────────────────────────────────────────────────────────────────────

const REGION_BY_STATE = {
  ME: "NE", NH: "NE", VT: "NE", MA: "NE", RI: "NE", CT: "NE",
  NY: "NE", NJ: "NE", PA: "NE",
  DE: "S", MD: "S", DC: "S", VA: "S", WV: "S", NC: "S",
  SC: "S", GA: "S", FL: "S", KY: "S", TN: "S", AL: "S",
  MS: "S", AR: "S", LA: "S", OK: "S", TX: "S",
  OH: "MW", MI: "MW", IN: "MW", IL: "MW", WI: "MW", MN: "MW",
  IA: "MW", MO: "MW", ND: "MW", SD: "MW", NE: "MW", KS: "MW",
  MT: "W", WY: "W", CO: "W", NM: "W", AZ: "W", UT: "W",
  NV: "W", ID: "W", WA: "W", OR: "W", CA: "W", AK: "W", HI: "W",
};

function sizeBand(size) {
  if (!size) return "unknown";
  if (size < 3000) return "small";
  if (size < 10000) return "medium";
  if (size < 25000) return "large";
  return "very_large";
}

export function scoreHiddenGem(school, ctx) {
  const { flagshipNet, dreamSchools, bracketValue } = ctx;
  const np = netPriceForBracket(school, bracketValue);
  const grad = school.graduationRate || 0;

  if (!np || grad < 0.6) return { eligible: false, score: 0, np, grad };

  // 1. Cost score: 0-50, max when net is half of flagship's net.
  let cost = 0;
  if (flagshipNet) {
    const ratio = np / flagshipNet;
    cost = ratio <= 0.5 ? 50 : Math.max(0, Math.round(50 * (1 - (ratio - 0.5) / 0.6)));
  } else {
    // No flagship match, use raw net price benchmark of $20K = 25 pts.
    cost = Math.max(0, Math.round(50 * (1 - np / 40000)));
  }

  // 2. Grad rate score: 0-30, scales linearly from 60% -> 95%+.
  const grad_score = Math.round(30 * Math.min(1, (grad - 0.6) / 0.35));

  // 3. Dream-school similarity: 0-20.
  let sim = 0;
  if (dreamSchools && dreamSchools.length) {
    const targetRegions = new Set(dreamSchools.map(d => REGION_BY_STATE[d.state] || "X"));
    const targetBands = new Set(dreamSchools.map(d => sizeBand(d.size)));
    if (targetRegions.has(REGION_BY_STATE[school.state] || "X")) sim += 12;
    if (targetBands.has(sizeBand(school.size))) sim += 8;
  } else {
    sim = 10; // neutral
  }

  return {
    eligible: true,
    score: cost + grad_score + sim,
    np,
    grad,
    cost,
    grad_score,
    sim,
  };
}

/**
 * Filter all schools to "hidden gems" for the given user, scored and
 * ranked. `excludeFlagship` true drops the user's own state flagship.
 */
export function findHiddenGems(allSchools, ctx, limit = 5) {
  const { flagshipId } = ctx;
  return allSchools
    .filter(s => s.id !== flagshipId)
    .map(s => ({ school: s, ...scoreHiddenGem(s, ctx) }))
    .filter(r => r.eligible)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// ────────────────────────────────────────────────────────────────────
// localStorage cache for AI responses
// Same inputs never call AI twice. 30-day TTL.
// ────────────────────────────────────────────────────────────────────

const CACHE_PREFIX = "aidwaive:cache:";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function makeKey(parts) {
  return CACHE_PREFIX + parts.map(p => String(p ?? "").toLowerCase().replace(/\s+/g, "_")).join("|");
}

function cacheGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { v, ts } = JSON.parse(raw);
    if (!ts || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

function cacheSet(key, v) {
  try {
    localStorage.setItem(key, JSON.stringify({ v, ts: Date.now() }));
  } catch {
    // localStorage might be full or disabled. Failing silently is fine.
  }
}

export async function fetchAffordabilityExplain({ schoolName, incomeBracket, netPrice, grade, monthly, residency }) {
  const key = makeKey(["aff", schoolName, incomeBracket, grade, residency || "", Math.round(netPrice / 100) * 100]);
  const cached = cacheGet(key);
  if (cached) return { ...cached, _cached: true };
  const r = await fetch(`${API}/affordability/explain`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      school_name: schoolName,
      income_bracket: incomeBracket,
      estimated_net_price: netPrice,
      grade,
      monthly_loan_payment: monthly,
      residency: residency || undefined,
    }),
  });
  const j = await r.json();
  cacheSet(key, j);
  return j;
}

export async function fetchHiddenGemBlurbs({ incomeBracket, homeState, dreamSchools, schools }) {
  // Cache key reflects the exact cohort of 5 schools so any change rebuilds.
  const ids = schools.map(s => s.name).sort().join(",");
  const key = makeKey(["gems", incomeBracket, homeState || "X", (dreamSchools || []).map(d => d.name).sort().join(","), ids]);
  const cached = cacheGet(key);
  if (cached) return { ...cached, _cached: true };
  const r = await fetch(`${API}/hidden-gems/blurbs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      income_bracket: incomeBracket,
      home_state: homeState,
      dream_schools: (dreamSchools || []).map(d => d.name),
      schools: schools.map(s => ({
        name: s.name,
        state: s.state,
        type: s.type,
        size: s.size,
        grad_rate: s.graduationRate,
        net_price: s.np ?? netPriceForBracket(s, incomeBracket),
      })),
    }),
  });
  const j = await r.json();
  cacheSet(key, j);
  return j;
}

/** Fetch the entire seed once with all bracket data, cache for 24h. */
export async function fetchAllColleges() {
  const cached = cacheGet(CACHE_PREFIX + "all-colleges-v2");
  if (cached) return cached;
  const r = await fetch(`${API}/netprice/all`);
  const j = await r.json();
  const rows = j.schools || [];
  cacheSet(CACHE_PREFIX + "all-colleges-v2", rows);
  return rows;
}

/** Fetch full school detail (with brackets) for a list of school IDs. */
export async function fetchSchoolDetails(ids) {
  return Promise.all(ids.map(async id => {
    const cacheKey = CACHE_PREFIX + "detail-" + id;
    const cached = cacheGet(cacheKey);
    if (cached) return cached;
    const r = await fetch(`${API}/netprice/${id}`);
    const j = await r.json();
    cacheSet(cacheKey, j);
    return j;
  }));
}
